import { useState } from "react";
import { ChevronRight, ChevronDown, Copy, Trash2, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { ContentBlockEditor } from "./ContentBlockEditor";
import { LIST, critFromText, CRIT_KEYS, CRIT_LABELS, BLOCK_META, newBlock, move, duplicateWithFreshIds } from "./roadmapHelpers";

function F({ label, children }) {
  return <label className="block text-[11px] text-zinc-400 space-y-1"><span>{label}</span>{children}</label>;
}

export function PhaseCard({ phase, idx, count, library, readOnly, onChange, onLibraryChange, onRemove, onDuplicate, onMoveUp, onMoveDown }) {
  const [open, setOpen] = useState(idx === 0);
  const set = (patch) => onChange({ ...phase, ...patch });
  const setCrit = (k, text) => set({ criteria: { ...phase.criteria, [k]: critFromText(text) } });
  const blocks = phase.content_blocks || [];
  const setBlocks = (b) => set({ content_blocks: b });
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="fl-card p-0 overflow-hidden" data-testid={`phase-card-${idx}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900/60">
        <button type="button" className="text-zinc-400 hover:text-white" onClick={() => setOpen((o) => !o)} data-testid={`phase-${idx}-toggle`}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="text-xs text-zinc-500 shrink-0">Phase {idx + 1}</span>
        <input className="fl-input text-sm flex-1" data-testid={`phase-title-${idx}`} disabled={readOnly} value={phase.title || ""} onChange={(e) => set({ title: e.target.value })} placeholder="Phase name" />
        <span className="text-[11px] text-zinc-500 shrink-0 hidden sm:inline">{blocks.length} item{blocks.length === 1 ? "" : "s"}</span>
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" className="fl-btn-ghost px-1.5 py-1" title="Move up" onClick={onMoveUp} data-testid={`phase-${idx}-up`} disabled={idx === 0}><ArrowUp size={13} /></button>
            <button type="button" className="fl-btn-ghost px-1.5 py-1" title="Move down" onClick={onMoveDown} data-testid={`phase-${idx}-down`} disabled={idx === count - 1}><ArrowDown size={13} /></button>
            <button type="button" className="fl-btn-ghost px-1.5 py-1" title="Duplicate phase" onClick={onDuplicate} data-testid={`phase-${idx}-duplicate`}><Copy size={13} /></button>
            <button type="button" className="fl-btn-ghost px-1.5 py-1" title="Delete phase" onClick={onRemove} data-testid={`phase-${idx}-remove`}><Trash2 size={13} /></button>
          </div>
        )}
      </div>

      {open && (
        <div className="p-3 space-y-3">
          <F label="Description"><textarea className="fl-input text-xs" rows={2} disabled={readOnly} value={phase.description ?? ""} onChange={(e) => set({ description: e.target.value })} data-testid={`phase-${idx}-description`} /></F>
          <F label="Goals (one per line)"><textarea className="fl-input text-xs" rows={2} disabled={readOnly} value={LIST(phase.goals)} onChange={(e) => set({ goals: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} data-testid={`phase-${idx}-goals`} /></F>

          <div className="grid md:grid-cols-2 gap-2">
            {CRIT_KEYS.map((k) => (
              <F key={k} label={`${CRIT_LABELS[k]} (one per line)`}>
                <textarea className="fl-input text-xs" rows={2} disabled={readOnly} value={LIST(phase.criteria?.[k])} onChange={(e) => setCrit(k, e.target.value)} data-testid={`phase-${idx}-crit-${k}`} />
              </F>
            ))}
          </div>

          <div className="space-y-2">
            <div className="text-xs text-zinc-300">Content blocks</div>
            {blocks.map((b, i) => (
              <ContentBlockEditor key={b.id || i} block={b} idx={i} library={library} readOnly={readOnly} onLibraryChange={onLibraryChange}
                onChange={(nb) => setBlocks(blocks.map((x, j) => (j === i ? nb : x)))}
                onRemove={() => setBlocks(blocks.filter((_, j) => j !== i))}
                onMoveUp={() => setBlocks(move(blocks, i, -1))}
                onMoveDown={() => setBlocks(move(blocks, i, 1))} />
            ))}
            {!blocks.length && <div className="text-xs text-zinc-600">No content yet.</div>}
            {!readOnly && (
              <div className="relative">
                <button type="button" className="fl-btn-ghost text-xs" onClick={() => setAddOpen((o) => !o)} data-testid={`phase-${idx}-add-block`}><Plus size={12} /> Add content block</button>
                {addOpen && (
                  <div className="absolute z-20 mt-1 rounded-lg border border-zinc-700 bg-zinc-950 p-1 shadow-xl">
                    {Object.entries(BLOCK_META).map(([type, m]) => (
                      <button key={type} type="button" className="block w-full text-left px-3 py-1.5 rounded text-xs text-zinc-200 hover:bg-zinc-800" data-testid={`phase-${idx}-add-${type}`}
                        onClick={() => { setBlocks([...blocks, newBlock(type)]); setAddOpen(false); }}>{m.label}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
